import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

const STATUS_LABELS = {
  new:       'New',
  contacted: 'Contacted',
  confirmed: 'Confirmed',
  declined:  'Declined',
};

const STATUS_COLORS = {
  new:       'bg-blue-50 text-blue-700 border-blue-200',
  contacted: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  confirmed: 'bg-green-50 text-green-700 border-green-200',
  declined:  'bg-red-50 text-red-600 border-red-200',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function CateringPanel() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expanded, setExpanded]   = useState(null);

  useEffect(() => { fetchInquiries(); }, []);

  async function fetchInquiries() {
    setLoading(true);
    if (isSupabaseConfigured()) {
      const { data } = await supabase
        .from('catering_inquiries')
        .select('*')
        .order('created_at', { ascending: false });
      setInquiries(data || []);
    } else {
      setInquiries([]);
    }
    setLoading(false);
  }

  async function updateStatus(id, status) {
    setInquiries(prev => prev.map(i => i.id === id ? { ...i, status } : i));
    await supabase.from('catering_inquiries').update({ status }).eq('id', id);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this catering inquiry? This cannot be undone.')) return;
    await supabase.from('catering_inquiries').delete().eq('id', id);
    setInquiries(prev => prev.filter(i => i.id !== id));
    if (expanded === id) setExpanded(null);
  }

  const filtered = inquiries.filter(i => statusFilter === 'all' || i.status === statusFilter);

  return (
    <div className="space-y-6 max-w-3xl">

      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif text-charcoal">Catering Inquiries</h2>
        <p className="text-sage-500 text-sm mt-1">Review and respond to event catering requests.</p>
      </div>

      {/* Status filter pills */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${
            statusFilter === 'all'
              ? 'bg-charcoal text-white'
              : 'bg-white text-sage-400 border border-sage-100 hover:border-sage-200'
          }`}
        >
          All ({inquiries.length})
        </button>
        {Object.entries(STATUS_LABELS).map(([key, label]) => {
          const count = inquiries.filter(i => i.status === key).length;
          if (count === 0) return null;
          return (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all border ${
                statusFilter === key
                  ? STATUS_COLORS[key]
                  : 'bg-white text-sage-400 border-sage-100 hover:border-sage-200'
              }`}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-blush-200 border-t-blush-400 rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-sage-100 p-12 text-center">
          <SafeIcon name="Inbox" className="text-sage-300 text-4xl mx-auto mb-3" />
          <p className="text-sage-400 italic">
            {statusFilter !== 'all' ? `No ${STATUS_LABELS[statusFilter].toLowerCase()} inquiries.` : 'No catering inquiries yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(inquiry => (
            <div key={inquiry.id} className="bg-white rounded-2xl border border-sage-100 overflow-hidden shadow-soft">
              {/* Row header */}
              <div
                className="p-5 cursor-pointer flex items-start justify-between gap-4"
                onClick={() => setExpanded(expanded === inquiry.id ? null : inquiry.id)}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-orange-50 rounded-full flex items-center justify-center shrink-0">
                    <SafeIcon name="Star" className="text-orange-400" />
                  </div>
                  <div>
                    <p className="font-bold text-charcoal">{inquiry.name}</p>
                    <p className="text-xs text-sage-400">
                      {inquiry.email}{inquiry.phone ? ` · ${inquiry.phone}` : ''}
                    </p>
                    <p className="text-xs text-sage-500 mt-1">
                      {inquiry.event_type || 'Event'}{' '}
                      · {inquiry.headcount ? `${inquiry.headcount} guests` : 'Headcount TBD'}{' '}
                      · {formatDate(inquiry.event_date)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_COLORS[inquiry.status]}`}>
                    {STATUS_LABELS[inquiry.status]}
                  </span>
                  <SafeIcon
                    name={expanded === inquiry.id ? 'ChevronUp' : 'ChevronDown'}
                    className="text-sage-400"
                  />
                </div>
              </div>

              {/* Expanded detail */}
              {expanded === inquiry.id && (
                <div className="border-t border-sage-100 p-5 space-y-4 bg-sage-50/30">
                  {inquiry.notes && (
                    <div>
                      <p className="text-xs font-bold text-sage-400 uppercase tracking-wider mb-1">Their Notes</p>
                      <p className="text-sm text-charcoal whitespace-pre-wrap bg-white rounded-xl p-3 border border-sage-100">{inquiry.notes}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-xs font-bold text-sage-400 uppercase tracking-wider mb-2">Update Status</p>
                    <div className="flex gap-2 flex-wrap">
                      {Object.entries(STATUS_LABELS).map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => updateStatus(inquiry.id, key)}
                          className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all border ${
                            inquiry.status === key
                              ? STATUS_COLORS[key]
                              : 'bg-white text-sage-400 border-sage-200 hover:border-sage-300'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-1">
                    <p className="text-xs text-sage-400">
                      Submitted {new Date(inquiry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                    <button
                      onClick={() => handleDelete(inquiry.id)}
                      className="text-xs text-red-400 hover:text-red-500 font-bold flex items-center gap-1 transition-colors"
                    >
                      <SafeIcon name="Trash2" className="text-xs" />
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
